/**
 * x-cross-sell directive.
 *
 * Manages a cross-sell product item inside a product form. Handles variant
 * selection (both hover prefetch and confirmed selection), morphs the item
 * HTML when the variant changes, and injects the cross-sell item into the
 * cart add payload when the parent form is submitted.
 *
 * @options
 *   selector       - CSS selector for the element to morph on variant update
 *                    (default: ".cross-sell-item-inner").
 *   morphSelectors - Additional selectors passed to `$morph` to constrain the
 *                    morph operation (default: []).
 *
 * @example
 *   <div x-cross-sell="{ selector: '.cross-sell-item-inner' }">…</div>
 */
(() => {
  const root = (
    Alpine,
    el,
    { selector = ".cross-sell-item-inner", morphSelectors = [] },
  ) => {
    Alpine.bind(el, () => ({
      /**
       * Intercepts the global `adding-to-cart` event and pushes the cross-sell
       * item into the items array when the cross-sell checkbox is checked.
       */
      ["@adding-to-cart.window"]($event) {
        const { items, elements, quantity } = $event.detail;

        this.handleAddingToCart({ items, elements, quantity });
      },

      /**
       * Prefetches the variant URL when the user hovers over a variant option.
       */
      ["@variant-picker-intent.stop"](event) {
        const { intentProductVariantUrl } = event.detail;

        this.$app.$prefetchManager.prefetch(intentProductVariantUrl);
      },

      /** Reserved for future use on variant selection. */
      ["@variant-picker-selected.stop"]() {},

      /**
       * Morphs the cross-sell item body with updated variant HTML, then syncs
       * the hidden variant ID and quantity input names.
       */
      ["@variant-picker-updated.stop"](event) {
        const { html, variant } = event.detail;

        const existingElement = this.$el.querySelector(selector);
        const newElement = html.querySelector(selector);

        this.$morph(existingElement, newElement, {
          morphSelectors,
        });

        if (this.$refs.crossSellItemFields) {
          this.updateInputs(variant.id);
        }
      },

      ["x-data"]() {
        return {
          /**
           * Builds the section rendering URL for the given variant.
           * @param {{ variantId: string, productUrl: string }} param0
           * @returns {URL} The constructed request URL.
           */
          buildRequestUrl({ variantId, productUrl }) {
            const url = new URL(`${window.location.origin}${productUrl}`);

            url.searchParams.set("variant", variantId);
            url.searchParams.set("section_id", "render-cross-sell-product");

            return url;
          },

          /**
           * Prefetches the cross-sell product HTML for the hovered variant choice.
           * Uses `data-variant-id` and `data-connected-product-url` from the
           * event target.
           */
          intentVariantChoice() {
            const target = this.$event.target;
            const { variantId, connectedProductUrl } = target.dataset;

            if (!variantId || !connectedProductUrl) {
              return;
            }

            const url = this.buildRequestUrl({
              variantId,
              productUrl: connectedProductUrl,
            });

            this.$app.$prefetchManager.prefetch(url.toString());
          },

          /**
           * Fetches and morphs the cross-sell item HTML when a variant choice is
           * confirmed. Updates the hidden inputs to reflect the new variant.
           * @returns {Promise<void>}
           */
          async selectedVariantChoice() {
            const target = this.$event.target;
            const { variantId, connectedProductUrl } = target.dataset;

            if (!variantId || !connectedProductUrl) {
              return;
            }

            const url = this.buildRequestUrl({
              variantId,
              productUrl: connectedProductUrl,
            });

            const responseText = await this.$app.$prefetchManager
              .fetchLatest(
                url.toString(),
                "cross-sell-variant-as-choice@update-section",
                {
                  noCache: this.noCache,
                },
              )
              .then((response) => response.text())
              .catch((error) => {
                if (error.name === "AbortError") {
                  console.warn("Fetch aborted by user.");
                } else {
                  console.error(error);
                }
              });

            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );

            if (html) {
              const existingElement = this.$root.querySelector(selector);
              const newElement = html.querySelector(selector);

              this.$morph(existingElement, newElement, {
                morphSelectors,
              });
            }

            this.updateInputs(variantId);
          },

          /**
           * Adds this cross-sell item to the add-to-cart items array when the
           * cross-sell checkbox is checked. Reads line item properties from
           * hidden inputs inside the component.
           * @param {{ items: object[], elements: Element[], quantity: number }} param0
           */
          handleAddingToCart({ items, elements, quantity }) {
            const variantId = this.$refs.crossSellItemVariantId.value;
            const checked = this.$refs.crossSellItemVariantId.checked;

            if (!checked) {
              return;
            }

            const properties =
              Array.from(this.$root.querySelectorAll("input[type=hidden]"))
                .filter((node) =>
                  node.name.startsWith(`cross_sell[${variantId}][properties]`),
                )
                ?.map((node) => ({
                  key: node.dataset.propertyKey,
                  value: node.value,
                }))
                .reduce((properties, { key, value }) => {
                  properties[key] = value;
                  return properties;
                }, {}) || {};

            // Use the cross-sell quantity if present, otherwise use the main quantity.
            const itemQuantity =
              this.$refs?.crossSellQuantity?.value || quantity;

            const item = {
              id: variantId,
              quantity: itemQuantity,
              properties,
            };

            if (item.id && item.quantity > 0) {
              items.push(item);
            }
          },

          /**
           * Updates the hidden variant ID input value and the cross-sell quantity
           * input name to match the newly selected variant.
           * @param {string} variantId - The selected variant ID.
           */
          updateInputs(variantId) {
            if (!variantId) return;

            this.$refs.crossSellItemVariantId.value = variantId;

            if (this.$refs.crossSellQuantity) {
              this.$refs.crossSellQuantity.name = `cross_sell[${variantId}][quantity]`;
            }
          },

          /**
           * Returns the currently checked input inside the cross-sell fields
           * container, or null if none is checked.
           * @returns {HTMLInputElement|null}
           */
          get selectedOption() {
            return this.$refs.crossSellItemFields?.querySelector(
              "input:checked",
            );
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "cross-sell",
      (el, { expression }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};
        root(Alpine, el, options);
      },
    );
  });
})();
